import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import throttle from 'lodash/throttle';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { useWindowResize } from '@app/helpers';
import { getEmojiEditions } from '@app/store/actions/editions';
import { loadWasms } from '@app/store/actions/emoji';
import { WasmType } from '@app/store/types/emoji';

import YatHorizontalScrollContainer from '../../Layout/HorizontalScrollContainer';
import YatAddEmojiButton from '../AddEmojiButton/AddEmojiButton';

import './EmojiSlider.scss';
import EmojiSliderDisabler from './EmojiSliderDisabler';
import EmojiSliderNav from './EmojiSliderNav';

const baseEmojiGroupList = [
    'Smileys & People',
    'Animals & Nature',
    'Food & Drink',
    'Activities',
    'Travel & Places',
    'Objects',
    'Symbols',
];

interface Props {
    disabled: boolean;
}

const EmojiSlider = ({ disabled = false }: Props) => {
    const component = useRef<HTMLDivElement>(null);
    const [threeEmojiRows, setThreeEmojiRows] = useState(true);
    const [vw, setVw] = useState(0);
    const [activeSection, setActiveSection] = useState(1);
    const [sliderSections, setSliderSections] = useState<string[]>([]);
    const sliderSectionRefs = useRef<Array<HTMLElement>>([]);
    const [emojiGroups, setEmojiGroups] = useState<Record<string, string[]>>({});
    const windowSize = useWindowResize();

    const dispatch = useAppDispatch();

    const { wasm } = useAppSelector(({ emoji }) => emoji);
    const { editions } = useAppSelector((state) => state.editions);

    useEffect(() => {
        if (windowSize !== undefined) {
            setThreeEmojiRows(window.matchMedia(`(max-width: 576px), (min-aspect-ratio: 2/1)`).matches);
            setVw(document.documentElement.clientWidth);
        }
    }, [windowSize]);

    useEffect(() => {
        dispatch(loadWasms(WasmType.EmojiWasm));
    }, [dispatch]);

    useEffect(() => {
        if (!editions || editions?.length === 0) {
            dispatch(getEmojiEditions());
        }
    }, [editions, dispatch]);

    useEffect(() => {
        if (wasm) {
            const groups = {};
            const filter = new wasm.CharacterSet(editions);
            const groupsList = filter.nonEmptyGroups();

            groupsList.forEach((group) => {
                groups[group] = filter.filter(group, 'Group');
            });

            setSliderSections(baseEmojiGroupList);
            setEmojiGroups(groups);
        }
    }, [wasm, editions]);

    const addRowBreakClass = (itemIndex: number, parentLength: number) => {
        const rows = threeEmojiRows ? 3 : 4;
        const itemsPerRow = Math.ceil(parentLength / rows);
        return Number.isInteger(itemIndex / itemsPerRow) ? '-row-break' : '';
    };

    // Add array of ref's for the emoji sections
    useEffect(() => {
        sliderSectionRefs.current = sliderSectionRefs.current.slice(0, Number(emojiGroups.length));
    }, [emojiGroups.length]);

    // When sections is ready, set scroll position to centering the first category
    useEffect(() => {
        const scrollToIndex = 0;
        if (sliderSectionRefs.current.length > scrollToIndex) {
            const element = sliderSectionRefs.current[scrollToIndex] as HTMLElement;
            const inline = window.matchMedia(`(max-width: 576px)`).matches ? 'start' : 'center';
            const options = { behavior: 'auto', inline: inline } as ScrollIntoViewOptions;
            element.scrollIntoView(options);
        }
    }, [emojiGroups]);

    // Calculate "active" emoji category section.
    const getActiveSection = useCallback(
        (e) => {
            const activeSection =
                sliderSectionRefs.current.length -
                [...sliderSectionRefs.current].reverse().findIndex((section) => {
                    return e.target.scrollLeft >= section.offsetLeft - vw / 2;
                });
            setActiveSection(activeSection);
        },
        [sliderSectionRefs, vw],
    );

    const throttledScroll = useMemo(() => throttle((e) => getActiveSection(e), 250), [getActiveSection]);

    // const throttledMethod = React.useMemo(
    //         () => _.throttle(abc, 500 ),
    //         [abc],
    //     );
    return (
        <div ref={component} className="emoji-slider">
            <YatHorizontalScrollContainer classes="emoji-slider__slider" onScroll={throttledScroll}>
                <div className="emoji-slider__slider-inner">
                    <div className="emoji-slider__category -spacer" />
                    {Object.keys(emojiGroups).length > 0 &&
                        baseEmojiGroupList.map((category, i) => (
                            <section
                                ref={(el: HTMLElement) => (sliderSectionRefs.current[i] = el)}
                                id={'emoji-slider-section-' + i}
                                key={`${threeEmojiRows}${i}`}
                                className="emoji-slider__category"
                                aria-label={category}
                            >
                                {emojiGroups[category].map((emoji, j) => (
                                    <YatAddEmojiButton
                                        key={j}
                                        emoji={emoji[0]}
                                        emojiName={emoji[1]}
                                        classes={`emoji-slider__emoji ${addRowBreakClass(
                                            j,
                                            emojiGroups[category].length,
                                        )}`}
                                    />
                                ))}
                            </section>
                        ))}
                    <div className="emoji-slider__category -spacer" />
                </div>
            </YatHorizontalScrollContainer>
            <EmojiSliderNav sections={sliderSections} activeSection={activeSection} />
            {disabled && <EmojiSliderDisabler />}
        </div>
    );
};

export default EmojiSlider;
